/** Utility to manage fullscreen mode on mobile devices. */

import { useCallback, useEffect, useState } from 'react';

export interface FullscreenAPI {
  requestFullscreen(): Promise<void>;
  exitFullscreen(): Promise<void>;
  isFullscreen(): boolean;
  isSupported(): boolean;
}

class FullscreenManager implements FullscreenAPI {
  private getFullscreenElement(): Element | null {
    return (
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );
  }

  private getRequestFullscreenMethod(element: Element): (() => Promise<void>) | null {
    return (
      element.requestFullscreen ||
      (element as any).webkitRequestFullscreen ||
      (element as any).webkitRequestFullScreen ||
      (element as any).mozRequestFullScreen ||
      (element as any).msRequestFullscreen
    );
  }

  private getExitFullscreenMethod(): (() => Promise<void>) | null {
    return (
      document.exitFullscreen ||
      (document as any).webkitExitFullscreen ||
      (document as any).webkitCancelFullScreen ||
      (document as any).mozCancelFullScreen ||
      (document as any).msExitFullscreen
    );
  }

  async requestFullscreen(): Promise<void> {
    try {
      const element = document.documentElement;
      const requestMethod = this.getRequestFullscreenMethod(element);

      if (requestMethod) {
        await requestMethod.call(element);
        return;
      }

      if (this.isIOSSafari()) {
        this.simulateFullscreenOnIOS();
        return;
      }

      throw new Error('Fullscreen não suportado neste dispositivo');
    } catch (error) {
      console.warn('Erro ao entrar em fullscreen:', error);

      if (this.isMobile()) {
        this.hideAddressBar();
      }

      throw error;
    }
  }

  async exitFullscreen(): Promise<void> {
    try {
      if (!this.isFullscreen()) {
        return;
      }

      const exitMethod = this.getExitFullscreenMethod();

      if (exitMethod) {
        await exitMethod.call(document);
        return;
      }

      if (this.isIOSSafari()) {
        this.exitSimulatedFullscreenOnIOS();
        return;
      }

      throw new Error('Não foi possível sair do fullscreen');
    } catch (error) {
      console.warn('Erro ao sair do fullscreen:', error);
      throw error;
    }
  }

  isFullscreen(): boolean {
    return !!this.getFullscreenElement();
  }

  isSupported(): boolean {
    const element = document.documentElement;
    const requestMethod = this.getRequestFullscreenMethod(element);
    return !!requestMethod;
  }

  private isIOSSafari(): boolean {
    const userAgent = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(userAgent) && /Safari/.test(userAgent) && !/CriOS/.test(userAgent);
  }

  private isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  private simulateFullscreenOnIOS(): void {
    const metaViewport = document.querySelector('meta[name="viewport"]');
    if (metaViewport) {
      metaViewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
      );
    }

    document.documentElement.classList.add('ios-fullscreen-simulation');

    this.hideAddressBar();

    window.dispatchEvent(new CustomEvent('fullscreenchange'));
  }

  private exitSimulatedFullscreenOnIOS(): void {
    const metaViewport = document.querySelector('meta[name="viewport"]');
    if (metaViewport) {
      metaViewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0'
      );
    }

    document.documentElement.classList.remove('ios-fullscreen-simulation');

    window.dispatchEvent(new CustomEvent('fullscreenchange'));
  }

  private hideAddressBar(): void {
    setTimeout(() => {
      window.scrollTo(0, 1);
    }, 100);
  }

  onFullscreenChange(callback: (isFullscreen: boolean) => void): () => void {
    const handleChange = () => {
      callback(this.isFullscreen());
    };

    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);
    document.addEventListener('mozfullscreenchange', handleChange);
    document.addEventListener('MSFullscreenChange', handleChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
      document.removeEventListener('mozfullscreenchange', handleChange);
      document.removeEventListener('MSFullscreenChange', handleChange);
    };
  }
}

export const fullscreenManager = new FullscreenManager();

export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    setIsSupported(fullscreenManager.isSupported());
    setIsFullscreen(fullscreenManager.isFullscreen());

    const unsubscribe = fullscreenManager.onFullscreenChange((fullscreen) => {
      setIsFullscreen(fullscreen);
    });

    return unsubscribe;
  }, []);

  const enterFullscreen = useCallback(async () => {
    try {
      await fullscreenManager.requestFullscreen();
    } catch (error) {
      console.error('Erro ao entrar em fullscreen:', error);
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    try {
      await fullscreenManager.exitFullscreen();
    } catch (error) {
      console.error('Erro ao sair do fullscreen:', error);
    }
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (isFullscreen) {
      await exitFullscreen();
    } else {
      await enterFullscreen();
    }
  }, [isFullscreen, enterFullscreen, exitFullscreen]);

  return {
    isFullscreen,
    isSupported,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
  };
}

